/**
 * React Native WebView Bridge Utility
 *
 * Safe wrapper around window.ReactNativeWebView.postMessage.
 * window.ReactNativeWebView is injected by React Native WebView — it does NOT exist
 * in a regular browser. This helper silently no-ops when running standalone so the
 * portal works identically in both contexts.
 *
 * Message types used by ArkaGo:
 *   PORTAL_READY        — portal finished loading, ready for user interaction
 *   PORTAL_SAVING       — save in progress, ArkaGo disables the Exit button
 *   PORTAL_SAVED        — save succeeded, ArkaGo fetches financial data + navigates back
 *   PORTAL_SAVE_FAILED  — save failed, ArkaGo re-enables Exit + shows error alert
 *   PORTAL_EXIT         — user clicked "Back to App" button, ArkaGo navigates back
 *
 * Usage:
 *   import { postToRN } from '@/utils/rnBridge';
 *   postToRN('PORTAL_READY');
 *   postToRN('PORTAL_SAVED', { contactId: '1146' });
 *   postToRN('PORTAL_EXIT', { contactId: '1146' });
 */

export function postToRN(type, payload = {}) {
    const message = { type, payload };
    const hasRNBridge = window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === 'function';
    
    if (hasRNBridge) {
        window.ReactNativeWebView.postMessage(JSON.stringify(message));
    }
}

/**
 * Intercepts console.log / warn / error and forwards each entry to the RN app
 * via PORTAL_LOG so they appear in the app's debug console.
 * Safe to call multiple times — guards against double-setup.
 * Call this once in app mode (e.g., App.vue created()).
 *
 * App-side handler:
 *   case 'PORTAL_LOG': console[payload.level](payload.message); break;
 */
export function setupConsoleForward() {
    if (window.__arkaConsoleForwarded) return;
    window.__arkaConsoleForwarded = true;

    const origLog   = console.log.bind(console);
    const origWarn  = console.warn.bind(console);
    const origError = console.error.bind(console);

    function fwd(level, args) {
        try {
            const msg = Array.from(args).map(a => {
                if (a instanceof Error) return `${a.name}: ${a.message}`;
                try { return typeof a === 'object' ? JSON.stringify(a, null, 0) : String(a); }
                catch { return String(a); }
            }).join(' ');
            // Fire-and-forget via setTimeout to avoid blocking execution
            setTimeout(() => postToRN('PORTAL_LOG', { level, message: msg }), 0);
        } catch (_) { /* never throw from logger */ }
    }

    console.log   = (...a) => { origLog(...a);   fwd('log',   a); };
    console.warn  = (...a) => { origWarn(...a);  fwd('warn',  a); };
    console.error = (...a) => { origError(...a); fwd('error', a); };
}
